import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';

import shotService from '@services/shotService';
import { ShotType, SpeedLevel } from '@types/index';

// Validation schema
const evaluationSchema = z.object({
  shot_type_id: z.string().min(1, 'Shot type is required'),
  session_date: z.string().min(1, 'Session date is required'),
  session_type: z.enum(['live_practice', 'video_submission']),
  total_attempts: z.number().min(1, 'Total attempts must be at least 1'),
  successful_attempts: z.number().min(0, 'Successful attempts must be non-negative'),
  failed_attempts: z.number().min(0, 'Failed attempts must be non-negative'),
  accuracy_score: z.number().min(0).max(10, 'Accuracy score must be between 0 and 10'),
  speed_level: z.enum(['slow', 'medium', 'fast', 'very_fast']),
  speed_score: z.number().min(0).max(10, 'Speed score must be between 0 and 10'),
  video_url: z.string().url().optional().or(z.literal('')),
  notes: z.string().optional(),
});

type EvaluationFormData = z.infer<typeof evaluationSchema>;

interface ShotEvaluationFormProps {
  playerId: string;
  onSuccess?: () => void;
}

const ShotEvaluationForm: React.FC<ShotEvaluationFormProps> = ({ 
  playerId, 
  onSuccess 
}) => {
  const [shotTypes, setShotTypes] = useState<ShotType[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<EvaluationFormData>({
    resolver: zodResolver(evaluationSchema),
    defaultValues: {
      session_date: new Date().toISOString().split('T')[0],
      session_type: 'live_practice',
      total_attempts: 0,
      successful_attempts: 0,
      failed_attempts: 0,
      accuracy_score: 5,
      speed_level: 'medium',
      speed_score: 5,
    },
  });

  const watchedTotalAttempts = watch('total_attempts');
  const watchedSuccessfulAttempts = watch('successful_attempts');
  const watchedFailedAttempts = watch('failed_attempts');

  useEffect(() => {
    loadShotTypes();
  }, []);

  useEffect(() => {
    // Auto-calculate failed attempts
    const failed = watchedTotalAttempts - watchedSuccessfulAttempts;
    if (failed >= 0) {
      // Update form value without triggering validation
      reset({
        ...watch(),
        failed_attempts: failed,
      });
    }
  }, [watchedTotalAttempts, watchedSuccessfulAttempts]);

  const loadShotTypes = async () => {
    try {
      const types = await shotService.getShotTypes();
      setShotTypes(types);
    } catch (err: any) {
      toast.error('Failed to load shot types');
    }
  };

  const onSubmit = async (data: EvaluationFormData) => {
    try {
      setLoading(true);

      // Validate attempts
      if (data.total_attempts !== data.successful_attempts + data.failed_attempts) {
        toast.error('Total attempts must equal successful + failed attempts');
        return;
      }

      await shotService.createShotEvaluation({
        player_id: playerId,
        shot_type_id: data.shot_type_id,
        session_date: data.session_date,
        session_type: data.session_type,
        total_attempts: data.total_attempts,
        successful_attempts: data.successful_attempts,
        failed_attempts: data.failed_attempts,
        accuracy_score: data.accuracy_score,
        speed_level: data.speed_level,
        speed_score: data.speed_score,
        video_url: data.video_url || undefined,
        notes: data.notes,
      });

      toast.success('Shot evaluation recorded successfully!');
      reset();
      setOpen(false);
      onSuccess?.();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to record evaluation');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    reset();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  return (
    <>
      <Button variant="contained" onClick={handleOpen}>
        Record Shot Practice
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Record Shot Practice Session</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="shot_type_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.shot_type_id}>
                      <InputLabel>Shot Type</InputLabel>
                      <Select {...field} label="Shot Type">
                        {shotTypes.map((shot) => (
                          <MenuItem key={shot.id} value={shot.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{shot.name}</span>
                              <Chip 
                                label={`Level ${shot.difficulty_rating}`} 
                                size="small" 
                                color={shot.difficulty_rating <= 3 ? 'success' : 
                                       shot.difficulty_rating <= 6 ? 'warning' : 'error'}
                              />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                {errors.shot_type_id && (
                  <Typography variant="caption" color="error">
                    {errors.shot_type_id.message}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="session_date"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Session Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.session_date}
                      helperText={errors.session_date?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="session_type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.session_type}>
                      <InputLabel>Session Type</InputLabel>
                      <Select {...field} label="Session Type">
                        <MenuItem value="live_practice">Live Practice</MenuItem>
                        <MenuItem value="video_submission">Video Submission</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="speed_level"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.speed_level}>
                      <InputLabel>Speed Level</InputLabel>
                      <Select {...field} label="Speed Level">
                        <MenuItem value="slow">Slow</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="fast">Fast</MenuItem>
                        <MenuItem value="very_fast">Very Fast</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="total_attempts"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Total Attempts"
                      type="number"
                      fullWidth
                      error={!!errors.total_attempts}
                      helperText={errors.total_attempts?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="successful_attempts"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Successful Attempts"
                      type="number"
                      fullWidth
                      error={!!errors.successful_attempts}
                      helperText={errors.successful_attempts?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="failed_attempts"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Failed Attempts"
                      type="number"
                      fullWidth
                      disabled
                      error={!!errors.failed_attempts}
                      helperText={errors.failed_attempts?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="accuracy_score"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        Accuracy Score: {field.value}/10
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(field.value / 10) * 100}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <TextField
                        {...field}
                        type="number"
                        fullWidth
                        sx={{ mt: 1 }}
                        inputProps={{ min: 0, max: 10, step: 0.1 }}
                        error={!!errors.accuracy_score}
                        helperText={errors.accuracy_score?.message}
                      />
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="speed_score"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        Speed Score: {field.value}/10
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(field.value / 10) * 100}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <TextField
                        {...field}
                        type="number"
                        fullWidth
                        sx={{ mt: 1 }}
                        inputProps={{ min: 0, max: 10, step: 0.1 }}
                        error={!!errors.speed_score}
                        helperText={errors.speed_score?.message}
                      />
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="video_url"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Video URL (Optional)"
                      fullWidth
                      error={!!errors.video_url}
                      helperText={errors.video_url?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes (Optional)"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!errors.notes}
                      helperText={errors.notes?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Recording...' : 'Record Evaluation'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default ShotEvaluationForm;

